import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Download, Printer, Image as ImageIcon } from "lucide-react";
import { FlyerPreview } from "@/components/marketing/FlyerPreview";

const FIELDS = [["shop_name", "Shop name"], ["contact_name", "Representative name"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"], ["address", "Address"], ["bike_path", "Bike path name"]];

const errText = async (e) => {
  const d = e.response?.data;
  if (d instanceof Blob) { try { return apiError(JSON.parse(await d.text()).detail); } catch { return "Download failed"; } }
  return apiError(d?.detail);
};

export const PersonalizeDialog = ({ asset, onClose }) => {
  const [form, setForm] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [remember, setRemember] = useState(true);
  const [savedOnce, setSavedOnce] = useState(false);
  const [bikePaths, setBikePaths] = useState([]);
  const loadDefaults = () => {
    setLoadErr("");
    api.get("/marketing/personalize/defaults").then(({ data }) => {
      setHasLogo(data.has_logo);
      setSavedOnce(data.saved);
      setBikePaths(data.bike_paths || []);
      setForm({ shop_name: data.shop_name, contact_name: data.contact_name || "", phone: data.phone, email: data.email, website: data.website, address: data.address, bike_path: data.bike_path || "", include_logo: data.has_logo && data.include_logo !== false });
    }).catch((e) => setLoadErr(apiError(e.response?.data?.detail)));
  };
  useEffect(() => {
    if (!asset) { setForm(null); return; }
    loadDefaults();
  }, [asset]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (layout = "single") => {
    if (!form.shop_name.trim()) return toast.error("Shop name is required");
    setBusy(true);
    try {
      if (remember) await api.put("/marketing/personalize/defaults", form).catch(() => toast.error("Couldn't save your details, but your flyer will still download"));
      const res = await api.post(`/marketing/assets/${asset.id}/personalized`, form, { params: { layout }, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "flyer.pdf";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(layout === "png" ? "Your PNG is downloading" : asset.postcard ? "Your personalized postcard is downloading" : "Your personalized flyer is downloading"); onClose();
    } catch (e) { toast.error(await errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!asset} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="personalize-dialog">
        <DialogHeader>
          <DialogTitle>Personalize: {asset?.title}</DialogTitle>
          <DialogDescription>{asset?.strip === false ? "Your details fill in the shop name, bike path, return address and contact details on this piece." : "These details are printed in a contact strip along the bottom of every page, and fill in any representative name, phone and email placeholders on the flyer."}{savedOnce ? " Filled in from your saved flyer details." : ""}</DialogDescription>
        </DialogHeader>
        {loadErr ? (
          <div className="py-6 text-center space-y-3" data-testid="personalize-load-error"><div className="text-sm text-destructive">{loadErr}</div><Button size="sm" variant="outline" onClick={loadDefaults} data-testid="personalize-retry-btn">Try again</Button></div>
        ) : !form || !asset ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <div className="grid md:grid-cols-2 gap-6">
          <FlyerPreview assetId={asset.id} form={form} strip={asset.strip !== false} />
          <div className="space-y-3">
            {FIELDS.map(([k, l]) => (
              <div key={k} className="space-y-1"><Label>{l}</Label><Input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`personalize-${k.replace("_", "-")}`} list={k === "bike_path" ? "personalize-bike-paths" : undefined} placeholder={k === "bike_path" ? "e.g. East Bay Bike Path" : undefined} /></div>
            ))}
            <datalist id="personalize-bike-paths">{bikePaths.map((b) => <option key={b} value={b} />)}</datalist>
            <label className={`flex items-center gap-2 text-sm ${hasLogo ? "" : "opacity-60"}`}>
              <Switch checked={form.include_logo} disabled={!hasLogo} onCheckedChange={(v) => setForm((f) => ({ ...f, include_logo: v }))} data-testid="personalize-include-logo" />
              {hasLogo ? "Include my shop logo" : "No shop logo yet — add one in Account Settings"}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={remember} onCheckedChange={setRemember} data-testid="personalize-remember" />
              Save these details for future flyers
            </label>
          </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="personalize-cancel">Cancel</Button>
          {asset?.postcard && <Button variant="outline" onClick={() => download("png")} disabled={!form || busy} data-testid="personalize-png-btn" title="For print shops that can't use PDF files"><ImageIcon className="w-4 h-4 mr-2" />Download PNG</Button>}
          {asset?.postcard && <Button variant="outline" onClick={() => download("sheet")} disabled={!form || busy} data-testid="personalize-sheet-btn"><Printer className="w-4 h-4 mr-2" />Print sheet (2 per page)</Button>}
          <Button onClick={() => download()} disabled={!form || busy} data-testid="personalize-download-btn">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}{asset?.postcard ? "Download postcard" : "Download my flyer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
